import {
  Box,
  Grid,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import { createRef, ChangeEvent, useEffect } from "react";

import { useContextLeads } from "src/Contexts/Leads/LeadsContext";
import { IInputCreateLeads } from "src/Contexts/Leads/LeadsTypes";
import { AppTextField } from "src/Pages/components/AppFormComponents/AppTextField";
import { apenasNumeros } from "src/shared/Utils/FormatMoney";
import { cpfOurCnpj } from "src/shared/Utils/MaskCpfCnpj";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useIsValidNumberWhats } from "src/shared/Hooks/useIsValidNumberWhats";

export const FormLeads = () => {
  const { valuesInputsLeads, setValuesInputsLeads } = useContextLeads();

  const refOrigem = createRef<HTMLInputElement>();
  const handleFocusedID = () => refOrigem.current?.focus();

  const {
    isWhatsValid,
    handle: handleIsValidWhats,
    isLoading: isLoadingValidationWhats,
  } = useIsValidNumberWhats();

  const isWhatsLength = apenasNumeros(valuesInputsLeads?.whats)?.length === 11;
  const isNotWhatsValid = !isWhatsValid && isWhatsLength;

  useEffect(() => {
    if (isWhatsLength) {
      handleIsValidWhats(valuesInputsLeads.whats);
    }
  }, [valuesInputsLeads.whats]);

  useEffect(() => {
    handleFocusedID();
  }, []);

  const handleChange =
    (prop: keyof IInputCreateLeads, option?: "cpf" | "phone") =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (option === "phone") {
        setValuesInputsLeads((eventPrev) => ({
          ...eventPrev,
          [prop]: cpfOurCnpj({ text: event.target.value, type: "phone" }),
        }));
      } else if (option === "cpf") {
        setValuesInputsLeads((eventPrev) => ({
          ...eventPrev,
          [prop]: cpfOurCnpj({ text: event.target.value, type: "cpf" }),
        }));
      } else {
        setValuesInputsLeads((eventPrev) => ({
          ...eventPrev,
          [prop]: event.target.value,
        }));
      }
    };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AppTextField
          fullWidth
          inputRef={refOrigem}
          label="Nome completo *"
          placeholder="João da Silva"
          value={valuesInputsLeads?.name}
          onChange={handleChange("name")}
        />
      </Grid>

      <Grid item xs={12}>
        <AppTextField
          fullWidth
          label="Email"
          placeholder="lead@lead.com"
          value={valuesInputsLeads?.email}
          onChange={handleChange("email")}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AppTextField
            fullWidth
            label="Whats"
            placeholder="(11) 9-8070-1020"
            value={valuesInputsLeads?.whats}
            onChange={handleChange("whats", "phone")}
            sxStyle={{ mr: "8px" }}
          />
          {isLoadingValidationWhats ? (
            <CircularProgress color="inherit" size="1em" />
          ) : (
            <WhatsAppIcon
              fontSize="small"
              color={isNotWhatsValid ? "error" : "success"}
            />
          )}
        </Box>

        {isWhatsLength && (
          <Typography
            sx={{
              color: isNotWhatsValid ? "red" : "green",
              fontSize: "12px",
              mt: "3px",
              ml: "8px",
            }}
          >
            {isNotWhatsValid
              ? "Número de WhatsApp não é valido"
              : "Número de WhatsApp valido"}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <AppTextField
          fullWidth
          label="Telefone"
          placeholder="(11) 9-8070-1020"
          value={valuesInputsLeads?.telefone1}
          onChange={handleChange("telefone1", "phone")}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <AppTextField
          fullWidth
          select
          label="Nível de interesse *"
          value={valuesInputsLeads?.interestLevel}
          onChange={handleChange("interestLevel")}
        >
          <MenuItem value="HIGH">Alto</MenuItem>
          <MenuItem value="MEDIUM">Médio</MenuItem>
          <MenuItem value="LOW">Baixo</MenuItem>
        </AppTextField>
      </Grid>

      <Grid item xs={6} md={4}>
        <AppTextField
          fullWidth
          select
          label="Status lead *"
          value={valuesInputsLeads?.status}
          onChange={handleChange("status")}
        >
          <MenuItem value="NEW">Novo</MenuItem>
          <MenuItem value="IN_CONTACT">Em contato</MenuItem>
          <MenuItem value="PROPOSAL_SENT">Proposta enviada</MenuItem>
          <MenuItem value="PROGRESS">Em andamento</MenuItem>
          <MenuItem value="FUTURE_RECONTACT">Futuro contato</MenuItem>
          <MenuItem value="DISQUALIFIED">Desqualificado</MenuItem>
          <MenuItem value="LOST">Perdido</MenuItem>
        </AppTextField>
      </Grid>

      <Grid item xs={12} md={4}>
        <AppTextField
          fullWidth
          select
          label="Fonte do lead *"
          value={valuesInputsLeads?.sourceCollection}
          onChange={handleChange("sourceCollection")}
        >
          <MenuItem value="INDICATION">Indicação</MenuItem>
          <MenuItem value="GOOGLE">Google</MenuItem>
          <MenuItem value="FORM">Formulários</MenuItem>
          <MenuItem value="LADING_PAGE">Landing Page</MenuItem>

          <MenuItem value="FACEBOOK">FaceBook</MenuItem>
          <MenuItem value="INSTAGRAM">Instagram </MenuItem>
          <MenuItem value="LINKEDIN">LinkeDIn</MenuItem>

          <MenuItem value="EMAIL_MARKETING">Email</MenuItem>
          <MenuItem value="MARKETING">Marketing</MenuItem>
          <MenuItem value="DUTY">Plantão</MenuItem>
          <MenuItem value="STREET">Rua</MenuItem>
          <MenuItem value="LISTING">Listagem</MenuItem>
        </AppTextField>
      </Grid>

      <Grid item xs={6} md={4}>
        <AppTextField
          fullWidth
          select
          label="Tipo ( PF | PJ ) *"
          value={valuesInputsLeads?.entityType}
          onChange={handleChange("entityType")}
        >
          <MenuItem value=" ">Não informar</MenuItem>
          <MenuItem value="INDIVIDUAL">Individual</MenuItem>
          <MenuItem value="COMPANY">Empresa</MenuItem>
        </AppTextField>
      </Grid>

      <Grid item xs={12} md={4}>
        <AppTextField
          fullWidth
          label="CPF"
          placeholder="760.400.145.58"
          value={valuesInputsLeads?.cpf}
          onChange={handleChange("cpf", "cpf")}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <AppTextField
          fullWidth
          type="date"
          label="Data de nascimento"
          value={valuesInputsLeads?.birthDate}
          onChange={handleChange("birthDate")}
        />
      </Grid>

      <Grid item xs={12}>
        <AppTextField
          fullWidth
          label="Ação pendente"
          placeholder="Preciso enviar documentação"
          value={valuesInputsLeads?.pendingActions}
          onChange={handleChange("pendingActions")}
        />
      </Grid>

      <Grid item xs={6} md={4}>
        <AppTextField
          fullWidth
          select
          label="Tem plano"
          value={valuesInputsLeads?.isPlan}
          onChange={handleChange("isPlan")}
        >
          <MenuItem value="NOT">Não informar</MenuItem>
          <MenuItem value="TRUE">Sim</MenuItem>
          <MenuItem value="FALSE">Não</MenuItem>
        </AppTextField>
      </Grid>

      <Grid item xs={6} md={4}>
        <AppTextField
          fullWidth
          label="Data de captura"
          type="date"
          value={valuesInputsLeads?.captureAt}
          onChange={handleChange("captureAt")}
        />
      </Grid>

      <Grid item xs={12} md={12}>
        <AppTextField
          fullWidth
          label="Descrição do plano"
          value={valuesInputsLeads?.descriptionPlan}
          onChange={handleChange("descriptionPlan")}
        />
      </Grid>

      <Grid item xs={12}>
        <AppTextField
          fullWidth
          label="Motivo da desistência"
          value={valuesInputsLeads?.reasonAbandonment}
          onChange={handleChange("reasonAbandonment")}
        />
      </Grid>

      <Grid item xs={12}>
        <AppTextField
          fullWidth
          label="Observação"
          value={valuesInputsLeads?.observation}
          onChange={handleChange("observation")}
        />
      </Grid>
    </Grid>
  );
};
